import { useEffect, useState } from 'react'
import Modal from '../../../../../common/components/molecule/Modal/Modal'
import TextInput from '../../../../../common/components/atom/TextInput/TextInput'
import ButtonV2 from '../../../../../common/components/atom/ButtonV2/Button'
import { useToast } from '../../../../../common/providers/ToastProvider/useToast'
import { useUpdateLuminaire } from '../../../../../features/luminaire/hooks/useLuminaires'
import { Luminaire } from '../../../../../features/luminaire/types/luminaire.types'

interface EditZhagaNodeDialogProps {
  open: boolean
  luminaire: Luminaire | null
  onClose: () => void
}

export default function EditZhagaNodeDialog({
  open,
  luminaire,
  onClose,
}: EditZhagaNodeDialogProps) {
  const [devEui, setDevEui] = useState('')
  const [poleNumber, setPoleNumber] = useState('')
  const [phaseNumber, setPhaseNumber] = useState('')
  const [name, setName] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const toast = useToast()
  const updateLuminaire = useUpdateLuminaire()

  useEffect(() => {
    if (open && luminaire) {
      setDevEui(luminaire.lum_dev_eui)
      setPoleNumber(luminaire.lum_pole_number)
      setPhaseNumber(luminaire.lum_phase_number)
      setName(luminaire.lum_name)
      setErrorMessage(null)
    }
  }, [open, luminaire])

  const isSubmitDisabled =
    devEui.trim() === '' ||
    poleNumber.trim() === '' ||
    phaseNumber.trim() === ''

  const handleSubmit = () => {
    if (isSubmitDisabled || !luminaire) return
    setErrorMessage(null)
    updateLuminaire.mutate({
      lumId: luminaire.lum_id,
      luminaire: {
        ...luminaire,
        lum_name: name.trim() || 'Luminaire-' + devEui.slice(0, 8),
        lum_dev_eui: devEui.trim(),
        lum_pole_number: poleNumber.trim(),
        lum_phase_number: phaseNumber.trim(),
      },
      callbackFunc: ({ error }) => {
        if (error) {
          const status = error?.response?.status
          const backendMsg = error?.response?.data?.message
          const msg =
            backendMsg ??
            (status === 409 || status === 400
              ? 'devEUI déjà enregistré ou invalide'
              : 'Erreur lors de la modification')
          setErrorMessage(msg)
          toast.error(msg)
        } else {
          toast.success('Luminaire modifié')
          onClose()
        }
      },
    })
  }

  return (
    <Modal isOpen={open} handleClose={onClose}>
      <h2>Modifier le nœud Zhaga</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
        <TextInput
          label="devEUI *"
          value={devEui}
          onChange={(e) => {
            setDevEui(e.target.value)
            if (errorMessage) setErrorMessage(null)
          }}
        />
        <TextInput
          label="Numéro de poteau *"
          value={poleNumber}
          onChange={(e) => setPoleNumber(e.target.value)}
        />
        <TextInput
          label="Numéro de phase *"
          value={phaseNumber}
          onChange={(e) => setPhaseNumber(e.target.value)}
        />
        <TextInput
          label="Nom (optionnel)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {errorMessage && (
          <div
            role="alert"
            style={{
              background: '#fdecea',
              border: '1px solid #f5c2c0',
              color: '#b00020',
              padding: '0.5rem 0.75rem',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <ButtonV2
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="medium"
        >
          Annuler
        </ButtonV2>
        <ButtonV2
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="medium"
          disabled={isSubmitDisabled}
        >
          Enregistrer
        </ButtonV2>
      </div>
    </Modal>
  )
}
